import React from 'react';
import { motion } from 'framer-motion';
import { Zap, ArrowUp, RotateCw } from 'lucide-react';
import { branches } from '../slidesData';

const iconMap = {
  Zap: Zap,
  ArrowUp: ArrowUp,
  RotateCw: RotateCw,
};

export const BranchSelector = ({ onSelectBranch }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } },
  };

  return (
    <div
      className="h-full w-full flex flex-col justify-center items-center px-8 md:px-12 lg:px-20 py-12"
      data-testid="branch-selector"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-6xl w-full"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <p className="font-sans text-lg md:text-xl text-[#D31113] uppercase tracking-[0.3em] mb-4 font-semibold">
            Choose Your Path
          </p>
          <h2 className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-6">
            SELECT MOVEMENT TYPE
          </h2>
          <p className="font-sans text-xl text-gray-400 max-w-2xl mx-auto">
            The same physics and biology principles apply—now let's see how they translate to your specific sport.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {branches.map((branch) => {
            const Icon = iconMap[branch.icon];
            return (
              <motion.button
                key={branch.id}
                variants={itemVariants}
                onClick={() => onSelectBranch(branch.id)}
                className="group relative bg-[#2A1D45]/50 backdrop-blur-md border-2 border-white/10 rounded-2xl p-10 text-left hover:border-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#17072F]"
                style={{ 
                  '--branch-color': branch.color,
                }}
                data-testid={`branch-${branch.id}`}
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Glow effect on hover */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ backgroundColor: branch.color }}
                />
                
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300"
                  style={{ backgroundColor: `${branch.color}20` }}
                >
                  <Icon 
                    className="w-8 h-8 transition-colors duration-300" 
                    style={{ color: branch.color }}
                  />
                </div>

                {/* Content */}
                <h3 
                  className="pres-font-heading text-3xl md:text-4xl font-bold uppercase mb-2 transition-colors duration-300"
                  style={{ color: branch.color }}
                >
                  {branch.title}
                </h3>
                <p className="font-sans text-lg text-gray-300 mb-4">
                  {branch.subtitle}
                </p>
                <p className="font-sans text-sm text-gray-500 uppercase tracking-wider">
                  Tests: {branch.description}
                </p>

                {/* Arrow indicator */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: branch.color }}
                  >
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Border color on hover */}
                <div 
                  className="absolute inset-0 rounded-2xl border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ borderColor: branch.color }}
                />
              </motion.button>
            );
          })}
        </div>

        <motion.p 
          variants={itemVariants}
          className="text-center mt-12 text-gray-500 font-sans"
        >
          Press a card or use keys <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10 mx-1">1</kbd>
          <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10 mx-1">2</kbd>
          <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10 mx-1">3</kbd> to select
        </motion.p>
      </motion.div>
    </div>
  );
};
